import {
  CarbonAvoidanceProjectType,
  ProjectCertifications,
  ProjectMapperType,
  ProjectSizes,
  ProjectTag,
  ProjectTypes,
  ProjectType,
  ProjectSelector,
  ProjectSize,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import { useState } from "react";
import AnimateHeight from "react-animate-height";
import useMeasure from "react-use/lib/useMeasure";
import { ProjectInsightsCardProps } from "../ProjectInsightsCard/ProjectInsightsCard";
import { Typography } from "../Typography";
import { FilterButton } from "./FilterButton";
import { ProjectCount } from "./ProjectCount";
import { SupportedProjects } from "./SupportedProjects";

const FILTER_PIXEL_GAP = 12;
const MIN_BUTTON_PIXEL_WIDTH = 96;
const MAX_PROJECTS_PER_PAGE = 6;

const Container = styled.div`
  background-color: #fff;
  margin: 0 auto;
  padding: 0 24px;
  max-width: 1068px;

  @media ${(props) => props.theme.bp.desktopUp} {
    padding: 0;
    max-width: 1116px;
  }
`;

const FiltersContainer = styled.div`
  margin: 1rem 0;
  position: relative;

  // accordion button is anchored at the bottom in mobile
  padding-bottom: 48px;

  @media ${(props) => props.theme.bp.tabletUp} {
    padding-bottom: 0;
  }
`;

const FiltersWrapper = styled.div`
  max-width: 975px;
  margin: 0 auto 12px;
  display: flex;
  align-items: space-between;
  justify-content: space-between;
  gap: ${FILTER_PIXEL_GAP}px;
`;

const SecondaryFiltersWrapper = styled(AnimateHeight)`
  max-width: 975px;
  margin: 0 auto;

  // AnimateHeight creates an inner div
  // that holds the filter buttons
  > div {
    display: flex;
    position: relative;
    align-items: space-between;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: ${FILTER_PIXEL_GAP}px;
    margin-bottom: ${FILTER_PIXEL_GAP}px;

    @media ${(props) => props.theme.bp.tabletUp} {
      align-items: center;
      justify-content: center;
      padding-bottom: 0;
    }
  }
`;

const FilterReset = styled.div`
  text-align: right;
  border: none;
  margin-bottom: 1rem;
  cursor: pointer;
`;

const typesToDisplay: ProjectType[] = [
  ...Object.values(CarbonAvoidanceProjectType).filter(
    (type) =>
      ![
        CarbonAvoidanceProjectType.None,
        CarbonAvoidanceProjectType.All,
      ].includes(type)
  ),
  ProjectSelector.All,
];

const TAGS: ProjectMapperType[] = [
  ...ProjectTypes.filter((pt) => typesToDisplay.includes(pt.tag)),
  ...ProjectCertifications,
  ...ProjectSizes.filter((ps) => ps.tag !== ProjectSize.None),
];

export interface SupportedProjectsProps {
  projects: ProjectInsightsCardProps[];
}

export const SupportedProjectsFilter = ({
  projects,
}: SupportedProjectsProps) => {
  const [filtersWrapperRef, { width: filtersWidth }] =
    useMeasure<HTMLDivElement>();

  const [selectedTags, setSelectedTags] = useState<ProjectTag[]>([
    ProjectSelector.All,
  ]);

  const [isSecondaryFiltersOpen, setIsSecondaryFiltersOpen] =
    useState<boolean>(false);

  // leave space for the accordion button
  const maxPrimaryFilters = Math.floor(
    filtersWidth / (MIN_BUTTON_PIXEL_WIDTH + FILTER_PIXEL_GAP) - 2
  );
  const numberOfVisiblePrimaryFilters =
    maxPrimaryFilters > 2 ? maxPrimaryFilters : 2;

  const secondaryFiltersHeight = isSecondaryFiltersOpen ? undefined : 0;

  const constraints: Array<
    (
      selectedTags: ProjectTag[],
      currentProject: ProjectInsightsCardProps
    ) => boolean
  > = [
    (tags) => tags.includes(ProjectSelector.All),
    (tags, project) => tags.includes(project.size),
    (tags, project) => tags.includes(project.variant),
    (tags, project) =>
      project.certifications.some((cert) => selectedTags.includes(cert)),
  ];

  const filteredProjects =
    selectedTags.length > 0
      ? projects
          // keep only those projects that return true on any of the constraints
          .filter((project) =>
            constraints.some((constrain) => constrain(selectedTags, project))
          )
      : projects;

  const primaryFilters = TAGS.slice(0, numberOfVisiblePrimaryFilters);
  const secondaryFilters = TAGS.slice(numberOfVisiblePrimaryFilters);

  // only highlight the accordion button when secondary filters
  // are selected and the accordion is closed
  const isSecondaryFilterSelected =
    !isSecondaryFiltersOpen &&
    selectedTags.some((tag) =>
      secondaryFilters.find((filter) => filter.tag === tag)
    );

  return (
    <Container>
      {selectedTags.length === 1 &&
      selectedTags.includes(ProjectSelector.All) ? (
        <FilterReset>&nbsp;</FilterReset>
      ) : (
        <FilterReset onClick={() => setSelectedTags([ProjectSelector.All])}>
          <Typography use="dashboardMicroLinkRegularInline">
            Reset filters
          </Typography>
        </FilterReset>
      )}
      <FiltersContainer>
        <FiltersWrapper ref={filtersWrapperRef}>
          {primaryFilters.map((filter) => (
            <FilterButton
              key={filter.tag}
              tag={filter.tag}
              label={filter.title}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
            />
          ))}
          <FilterButton.Accordion
            aria-label="Show more filters"
            isSelected={isSecondaryFilterSelected}
            isOpen={isSecondaryFiltersOpen}
            onClick={() => setIsSecondaryFiltersOpen(!isSecondaryFiltersOpen)}
          />
        </FiltersWrapper>
        <SecondaryFiltersWrapper height={secondaryFiltersHeight} duration={400}>
          {secondaryFilters.map((filter) => (
            <FilterButton
              key={filter.tag}
              tag={filter.tag}
              label={filter.title}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
            />
          ))}
        </SecondaryFiltersWrapper>
      </FiltersContainer>
      <ProjectCount count={filteredProjects.length} />
      <SupportedProjects
        projects={filteredProjects}
        maxProjectsPerPage={MAX_PROJECTS_PER_PAGE}
      />
    </Container>
  );
};
